import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Download, Upload } from 'lucide-react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import { useToast } from '../../components/ui/Toast';
import { products, categories } from '../../data/mockData';

function ExpiryBadge({ days }) {
  if (days < 0) return <Badge variant="error">Просрочен</Badge>;
  if (days <= 3) return <Badge variant="urgent">{days} дн.</Badge>;
  if (days <= 7) return <Badge variant="warning">{days} дн.</Badge>;
  return <Badge variant="success">{days} дн.</Badge>;
}

export default function ProductCatalog() {
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterExpiry, setFilterExpiry] = useState('');

  const rootCats = categories.filter(c => !c.parentId);

  const filtered = products.filter(p => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.barcode.includes(search) && !p.article.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCat && p.category !== categories.find(c => c.id === filterCat)?.name) return false;
    if (filterStatus && p.status !== filterStatus) return false;
    if (filterExpiry === 'critical' && p.daysUntilExpiry > 3) return false;
    if (filterExpiry === 'expired' && p.daysUntilExpiry >= 0) return false;
    return true;
  });

  const columns = [
    {
      key: 'name', label: 'Товар',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: 8, background: '#F5F6FA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
            {row.category === 'Молоко' || row.category === 'Сметана' || row.category === 'Йогурт' ? '🥛' :
             row.category === 'Сыр' ? '🧀' :
             row.category === 'Масло' ? '🧈' :
             row.category === 'Хлебобулочные' ? '🍞' :
             row.category === 'Фрукты и овощи' ? '🥦' :
             row.category === 'Напитки' ? '🥤' :
             row.category === 'Мясо' ? '🥩' :
             row.category === 'Рыба' ? '🐟' : '📦'}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
            <div style={{ fontSize: 11, color: '#8E8E93' }}>{row.hierarchy}</div>
          </div>
        </div>
      )
    },
    { key: 'article', label: 'Артикул', render: v => <code style={{ fontSize: 11, background: '#F3F4F6', padding: '2px 6px', borderRadius: 4 }}>{v}</code> },
    { key: 'barcode', label: 'Штрих-код', render: v => <code style={{ fontSize: 11, fontFamily: 'monospace', color: '#4A6CF7' }}>{v}</code> },
    { key: 'category', label: 'Категория', render: v => <Badge variant="primary">{v}</Badge> },
    { key: 'price', label: 'Цена', render: (v, row) => (
      <div>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{v.toLocaleString()} сум</div>
        {row.discountPrice && <div style={{ fontSize: 11, color: '#FF3B30', fontWeight: 600 }}>↓ {row.discountPrice.toLocaleString()} сум</div>}
      </div>
    )},
    { key: 'stockQuantity', label: 'Остаток', render: (v, row) => <span style={{ fontWeight: 600 }}>{v} {row.unit}</span> },
    { key: 'daysUntilExpiry', label: 'Срок год.', render: v => <ExpiryBadge days={v} /> },
    { key: 'status', label: 'Статус', render: v => <Badge variant={v === 'active' ? 'active' : 'inactive'}>{v === 'active' ? 'Активен' : 'Неактивен'}</Badge> },
    {
      key: 'actions', label: '', sortable: false,
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
          <button onClick={() => toast('Товар отредактирован', 'success')} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', color: '#6B7280' }}>Редакт.</button>
        </div>
      )
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E' }}>Каталог товаров</h1>
          <p style={{ color: '#8E8E93', fontSize: 13, marginTop: 2 }}>Всего: {products.length} позиций</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" icon={<Upload size={14} />} size="sm" onClick={() => toast('Импорт запущен', 'success')}>Импорт CSV</Button>
          <Button variant="secondary" icon={<Download size={14} />} size="sm" onClick={() => toast('Экспорт начат', 'success')}>Экспорт</Button>
          <Button icon={<Plus size={14} />} size="sm" onClick={() => toast('Форма создания товара', 'success')}>Добавить товар</Button>
        </div>
      </div>

      <Card padding="0">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F5F6FA', borderRadius: 8, padding: '7px 12px', flex: 1, minWidth: 200 }}>
            <Search size={14} color="#9CA3AF" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск по названию, артикулу, штрих-коду..." style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%', fontFamily: 'inherit' }} />
          </div>
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ padding: '7px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">Все категории</option>
            {rootCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '7px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">Все статусы</option>
            <option value="active">Активен</option>
            <option value="inactive">Неактивен</option>
          </select>
          <select value={filterExpiry} onChange={e => setFilterExpiry(e.target.value)} style={{ padding: '7px 12px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">Любой срок</option>
            <option value="critical">Критический (≤3 дней)</option>
            <option value="expired">Просроченные</option>
          </select>
        </div>
        <Table columns={columns} data={filtered} onRowClick={row => navigate(`/products/${row.id}`)} emptyText="Товары не найдены" />
      </Card>
    </div>
  );
}
